import type { TFunction } from 'i18next';

import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import {
  CartItemGiftLimitFrequencyEnum,
  GiftTypeEnum,
} from '@sola/schema/dotcom/cart/gift.ts';
import type {
  ChildGiftLimitItem,
  DefaultPaymentOptionSet,
  MinMaxAmount,
} from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { GiftIconMap } from '@sola/shared-utils/cart/gifts.ts';

import { ButtonAction } from '../../types/gifts.ts';

export const givingFrequencyOptions = [
  {
    value: CommitmentFrequencyEnum.OneTime,
    translationKey: 'core:frequencies.oneTime',
  },
  {
    value: CommitmentFrequencyEnum.Annually,
    translationKey: 'core:frequencies.annually',
  },
];

export const giftAmountOptionsBase: Record<string, SelectItemProps> = {
  none: { value: '', text: 'Select an amount' },
  other: { value: 'Other', text: 'Other' },
};

/** TODO: pull from SDG / Contentful */
export const giftAmountsConfig: Record<GiftTypeEnum, number[]> = {
  [GiftTypeEnum.Birthday]: [
    10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150,
  ],
  [GiftTypeEnum.Child]: [
    10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150,
  ],
  [GiftTypeEnum.Family]: [
    25, 50, 75, 100, 150, 200, 250, 300, 400, 500, 600, 700, 800, 900, 1100,
  ],
  [GiftTypeEnum.Center]: [50, 100, 150, 200, 250, 300, 400, 500, 1000, 2000],
  [GiftTypeEnum.Final]: [
    25, 50, 75, 100, 150, 200, 250, 300, 400, 500, 600, 700, 800, 900, 1100,
  ],
};

/**
 * Maps a CommitmentFrequencyEnum value to the corresponding key in
 * DefaultPaymentOptionSet so we can look up min/max for the selected frequency.
 */
const frequencyToOptionKey: Record<
  CommitmentFrequencyEnum,
  keyof DefaultPaymentOptionSet
> = {
  [CommitmentFrequencyEnum.OneTime]: CartItemGiftLimitFrequencyEnum.OneTime,
  [CommitmentFrequencyEnum.Monthly]: CartItemGiftLimitFrequencyEnum.Monthly,
  [CommitmentFrequencyEnum.Quarterly]: CartItemGiftLimitFrequencyEnum.Quarterly,
  [CommitmentFrequencyEnum.Semiannually]:
    CartItemGiftLimitFrequencyEnum.Semiannually,
  [CommitmentFrequencyEnum.Annually]: CartItemGiftLimitFrequencyEnum.Annually,
};

/**
 * Resolves the min/max amounts for the custom "Other" field based on the
 * gift type limits and the currently selected frequency.
 */
export const getCustomAmountLimits = (
  giftTypeLimits: ChildGiftLimitItem | undefined,
  frequency: CommitmentFrequencyEnum,
): MinMaxAmount | undefined => {
  if (!giftTypeLimits) {
    return undefined;
  }

  const key = frequencyToOptionKey[frequency];
  return giftTypeLimits.paymentOptionsMinMax?.[key];
};

export const getGiftData = (
  t: TFunction<'my'>,
  giftType: GiftTypeEnum,
  commitment: GraphQLCommitment,
  buttonAction?: ButtonAction,
): {
  icon: IconNames;
  title: string;
  supportingText: string;
  addButtonText: string;
  giftAmounts: number[];
  giftAmountDefault: number;
  acceptCustomValue: boolean;
  frequencies: CommitmentFrequencyEnum[];
} => {
  const childName =
    commitment.participant?.names?.preferred ??
    commitment.participant?.name ??
    '';
  const gender = commitment.participant?.gender ?? '';

  const lowerType = giftType.toLowerCase();
  const icon = GiftIconMap[giftType];

  const isSetupRecurring = buttonAction === ButtonAction.SetupRecurring;
  const isEdit = buttonAction === ButtonAction.Edit;

  /**
   * OneTime is always available; Birthday additionally allows Annually.
   */
  const frequencies: CommitmentFrequencyEnum[] =
    giftType === GiftTypeEnum.Birthday
      ? [CommitmentFrequencyEnum.OneTime, CommitmentFrequencyEnum.Annually]
      : [CommitmentFrequencyEnum.OneTime];

  const addButtonText = isSetupRecurring
    ? t('gifts.dialog.setupRecurringAction')
    : t(`gifts.${lowerType}.dialog.${isEdit ? 'editAction' : 'action'}`);

  return {
    icon,
    title: t(`gifts.${lowerType}.dialog.title`, { childName }),
    supportingText: t(`gifts.${lowerType}.dialog.supportingText`, {
      context: gender,
      childName,
    }),
    addButtonText,
    // TODO: Disable options based on gift limits
    giftAmounts: giftAmountsConfig[giftType],
    // TODO: Get actual default
    giftAmountDefault: giftAmountsConfig[giftType][5],
    acceptCustomValue: true,
    frequencies,
  };
};
